import * as v from './rules';

/** Mirrors ResourceType and ResourceAudience on the server. */
export const AUDIENCES = [
  { value: 'TEACHER', label: 'Teachers' },
  { value: 'STUDENT', label: 'Students' },
  { value: 'BOTH', label: 'Teachers and students' },
];

// `kind` mirrors ResourceType.Kind, which is what the server's delivery rules key off:
// a VIDEO type goes to a player, an ACTIVITY to a sandboxed frame, everything else to
// a document viewer. It is here so the file picker and the replace-file confirmation
// can say which a file has to be without asking the server first.
const TYPES = [
  { value: 'TEACHER_MANUAL', label: 'Teacher Manual', permitted: 'TEACHER', kind: 'DOCUMENT' },
  { value: 'PPT', label: 'PPT', permitted: 'TEACHER', kind: 'DOCUMENT' },
  { value: 'LESSON_PLAN', label: 'Lesson Plan', permitted: 'TEACHER', kind: 'DOCUMENT' },
  { value: 'ASSESSMENT', label: 'Assessment', permitted: 'TEACHER', kind: 'DOCUMENT' },
  { value: 'VIDEO', label: 'Video', permitted: 'BOTH', kind: 'VIDEO' },
  // Kind VIDEO on purpose: an audio lesson opens in a player exactly as a video
  // does, and the file behind it is a video container, because Vimeo will not take
  // an audio-only file.
  { value: 'AUDIO', label: 'Audio', permitted: 'BOTH', kind: 'VIDEO' },
  { value: 'WORKSHEET', label: 'Worksheet', permitted: 'BOTH', kind: 'DOCUMENT' },
  // A zip holding index.html and the images and audio it references. BOTH, because a
  // teacher running the class has to be able to open what the class is given.
  { value: 'INTERACTIVE_ACTIVITY', label: 'Interactive Activity', permitted: 'BOTH', kind: 'ACTIVITY' },
  { value: 'ADDITIONAL_RESOURCE', label: 'Additional Resource', permitted: 'STUDENT', kind: 'DOCUMENT' },
];

const kindOf = (typeValue) =>
  TYPES.find((candidate) => candidate.value === typeValue)?.kind ?? null;

/** Whether a type is delivered to a player rather than to a document viewer. */
export const isVideoType = (typeValue) => kindOf(typeValue) === 'VIDEO';

/** Whether a type is a zip of web files rather than a single file. */
export const isActivityType = (typeValue) => kindOf(typeValue) === 'ACTIVITY';

/**
 * What the file picker should offer for a type.
 *
 * <p>One place rather than the two copies of the same ternary the upload form and the
 * replace control each carried: a third kind turned "video or not" into a wrong
 * answer in both, and in the same way.
 *
 * <p>Both the extension and the MIME type throughout: a file picker matches on either,
 * and which one a machine knows about varies — .mov and .zip are the two that differ
 * most between Windows and macOS.
 */
export const acceptFor = (typeValue) => {
  switch (kindOf(typeValue)) {
    case 'VIDEO':
      return '.mp4,.mov,video/mp4,video/quicktime';
    case 'ACTIVITY':
      return '.zip,application/zip,application/x-zip-compressed';
    default:
      return '.pdf,.pptx,.docx,.png,.jpg,.jpeg';
  }
};

/** The same rule the server applies: a type only goes to an audience it belongs to. */
export const allows = (typeValue, audience) => {
  const type = TYPES.find((candidate) => candidate.value === typeValue);
  return Boolean(type) && (type.permitted === 'BOTH' || type.permitted === audience);
};

export const typesFor = (audience) => TYPES.filter((type) => allows(type.value, audience));

export const typeSuitsAudience = (typeValue, audience) =>
  allows(typeValue, audience)
    ? null
    : `That type cannot be published to ${
        AUDIENCES.find((entry) => entry.value === audience)?.label ?? audience
      }`;

export const chapterName = (value) =>
  v.required('Chapter name')(value) ??
  (String(value).trim().length > 200 ? 'Chapter name must be 200 characters or fewer' : null);

export const resourceName = (value) =>
  v.required('File name')(value) ??
  (String(value).trim().length > 200 ? 'File name must be 200 characters or fewer' : null);

export const sequence = (value) => {
  const text = String(value ?? '').trim();
  if (!text) return 'Number is required';
  if (!/^\d+$/.test(text)) return 'Number must be a whole number';
  const amount = Number(text);
  if (amount < 1 || amount > 999) return 'Number must be between 1 and 999';
  return null;
};

export const SERVER_CODES = {
  chapter_sequence_taken: 'sequence',
  type_audience_mismatch: 'type',
  unsupported_file_type: 'name',
  file_too_large: 'name',
  file_required: 'name',
};
